import { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  useTheme,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Chip,
  IconButton,
  Divider,
  InputAdornment,
  Switch,
  FormControlLabel,
  Checkbox,
  ListItemText as MuiListItemText,
  OutlinedInput,
  alpha
} from '@mui/material';
import {
  CalendarMonth,
  People,
  RoomService,
  Schedule,
  Star,
  PersonAdd,
  Store,
  Close,
  Person,
  AccessTime,
  CurrencyPound
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useQuery, useMutation } from '@apollo/client';
import { GET_ALL_APPOINTMENTS, GET_ALL_STAFF, GET_ACTIVE_SERVICES, GET_ALL_RATINGS, GET_STORE_BY_ID } from '../graphql/queries';
import { CREATE_APPOINTMENT, CREATE_STAFF, CREATE_SERVICE } from '../graphql/mutations';
import { formatTime, formatTimeRange, getStoreTimezone, isSameDay, parseDate, convertDateTimeLocalToUTC } from '../utils/timezone';
import { categorizeAppointmentGroups, AppointmentGroup } from '../utils/appointmentUtils';
import { formatGBP } from '../utils/currency';
import LoadingSpinner from '../components/common/LoadingSpinner';
import StoreAvatar from '../components/common/StoreAvatar';
import { STAFF_ROLES, StaffFormData, StaffRole } from '../types/staff';
import TimeSlotPicker from '../components/TimeSlotPicker';
import AvailabilityDatePicker from '../components/AvailabilityDatePicker';

const Dashboard = () => {
  const theme = useTheme();
  const { storeId: paramStoreId } = useParams();
  const { storeId: authStoreId, staffId: currentStaffId } = useAuth();
  
  // Use store ID from URL params if available, otherwise use from auth context
  const storeId = paramStoreId || authStoreId;
  
  // State for staff filter - default to current logged-in user
  const [selectedStaffId, setSelectedStaffId] = useState<string>('all');

  // Dialog states for Quick Actions
  const [openAppointmentDialog, setOpenAppointmentDialog] = useState(false);
  const [openStaffDialog, setOpenStaffDialog] = useState(false);
  const [openServiceDialog, setOpenServiceDialog] = useState(false);

  // Form data for each dialog
  const [appointmentFormData, setAppointmentFormData] = useState({
    staffId: '',
    serviceId: '',
    scheduledDate: '',
    selectedTime: '',
    clientName: '',
    clientPhone: '',
    clientEmail: '',
    clientComment: '',
  });

  const [staffFormData, setStaffFormData] = useState<StaffFormData>({
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    password: '',
    title: '',
    services: [],
    role: STAFF_ROLES.STAFF,
    isActive: true,
  });

  const [serviceFormData, setServiceFormData] = useState({
    name: '',
    duration: 0,
    price: 0,
    isActive: true,
  });

  // Dialog handlers
  const handleOpenAppointmentDialog = () => {
    setAppointmentFormData({
      staffId: '',
      serviceId: '',
      scheduledDate: '',
      selectedTime: '',
      clientName: '',
      clientPhone: '',
      clientEmail: '',
      clientComment: '',
    });
    setOpenAppointmentDialog(true);
  };
  
  const handleCloseAppointmentDialog = () => setOpenAppointmentDialog(false);
  
  const handleOpenStaffDialog = () => {
    setStaffFormData({
      firstName: '',
      lastName: '',
      username: '',
      email: '',
      password: '',
      title: '',
      services: [],
      role: STAFF_ROLES.STAFF,
      isActive: true,
    });
    setOpenStaffDialog(true);
  };
  
  const handleCloseStaffDialog = () => setOpenStaffDialog(false);
  
  const handleOpenServiceDialog = () => {
    setServiceFormData({
      name: '',
      duration: 0,
      price: 0,
      isActive: true,
    });
    setOpenServiceDialog(true);
  };
  
  const handleCloseServiceDialog = () => setOpenServiceDialog(false);

  // Update localStorage when storeId from URL changes
  useEffect(() => {
    if (paramStoreId) {
      localStorage.setItem('storeId', paramStoreId);
    }
  }, [paramStoreId]);

  // Set default staff filter to current user when available
  useEffect(() => {
    if (currentStaffId && selectedStaffId === 'all') {
      setSelectedStaffId(currentStaffId);
    }
  }, [currentStaffId, selectedStaffId]);

  const { data: appointmentsData, loading: appointmentsLoading, refetch: refetchAppointments } = useQuery(GET_ALL_APPOINTMENTS, {
    variables: { storeId },
    skip: !storeId,
  });

  const { data: staffData, loading: staffLoading, refetch: refetchStaff } = useQuery(GET_ALL_STAFF, {
    variables: { storeId },
    skip: !storeId,
  });

  const { data: servicesData, loading: servicesLoading, refetch: refetchServices } = useQuery(GET_ACTIVE_SERVICES, {
    variables: { storeId },
    skip: !storeId,
  });

  const { data: ratingsData, loading: ratingsLoading } = useQuery(GET_ALL_RATINGS, {
    variables: { storeId },
    skip: !storeId,
  });

  const { data: storeData, loading: storeLoading } = useQuery(GET_STORE_BY_ID, {
    variables: { storeId },
    skip: !storeId,
  });

  // Mutations
  const [createAppointment] = useMutation(CREATE_APPOINTMENT);
  const [createStaff] = useMutation(CREATE_STAFF);
  const [createService] = useMutation(CREATE_SERVICE);

  // Submit handlers
  const handleAppointmentSubmit = async () => {
    // Validate form
    const validationErrors = validateAppointmentForm();
    if (validationErrors.length > 0) {
      console.error('Validation errors:', validationErrors);
      return;
    }

    try {
      // Combine date and time into a full datetime string
      const combinedDateTime = `${appointmentFormData.scheduledDate}T${appointmentFormData.selectedTime}:00`;
      const timezone = getStoreTimezone();
      const utcScheduledDate = convertDateTimeLocalToUTC(combinedDateTime, timezone);

      const result = await createAppointment({
        variables: {
          storeId,
          staffId: appointmentFormData.staffId,
          serviceId: appointmentFormData.serviceId,
          scheduledDate: utcScheduledDate,
          clientName: appointmentFormData.clientName,
          clientPhone: appointmentFormData.clientPhone,
          clientEmail: appointmentFormData.clientEmail,
          clientComment: appointmentFormData.clientComment,
        },
      });

      if (result.data?.createAppointment?.ok) {
        await refetchAppointments();
        handleCloseAppointmentDialog();
      } else {
        console.error('Error creating appointment');
      }
    } catch (error) {
      console.error('Error creating appointment:', error);
    }
  };

  const handleStaffSubmit = async () => {
    try {
      const result = await createStaff({
        variables: {
          ...staffFormData,
          sendInviteEmail: true,
          storeIds: [storeId],
        },
      });
      
      if (result.data?.createStaff?.ok) {
        console.log('Staff created successfully!');
        // Refetch staff to update the dashboard
        await refetchStaff();
        handleCloseStaffDialog();
      } else {
        console.error('Error creating staff');
      }
    } catch (error) {
      console.error('Error creating staff:', error);
    }
  };

  const handleServiceSubmit = async () => {
    try {
      const result = await createService({
        variables: {
          ...serviceFormData,
          storeId,
        },
      });
      
      if (result.data?.createService?.ok) {
        console.log('Service created successfully!');
        // Refetch services to update the dashboard
        await refetchServices();
        handleCloseServiceDialog();
      } else {
        console.error('Error creating service');
      }
    } catch (error) {
      console.error('Error creating service:', error);
    }
  };

  if (appointmentsLoading || staffLoading || servicesLoading || ratingsLoading || storeLoading) {
    return <LoadingSpinner message="Loading dashboard..." />;
  }

  const appointments = appointmentsData?.allAppointments || [];
  const staff = staffData?.allStaff || [];
  const services = servicesData?.activeServices || [];
  const ratings = ratingsData?.allRatings || [];
  const store = storeData?.storeDetails;
  
  // Filtering logic for appointment dialog
  const filteredServices = appointmentFormData.staffId
    ? services.filter((service: any) => 
        service.staffs?.some((staffMember: any) => staffMember.id === appointmentFormData.staffId)
      )
    : services;

  const filteredStaff = appointmentFormData.serviceId
    ? staff.filter((staffMember: any) => 
        staffMember.services?.some((service: any) => service.id === appointmentFormData.serviceId)
      )
    : staff;

  // Helper functions for appointment dialog
  const handleStaffChange = (staffId: string) => {
    setAppointmentFormData({ 
      ...appointmentFormData, 
      staffId,
      // Clear service if it's not available for the selected staff
      serviceId: staffId && appointmentFormData.serviceId ? 
        (services.some((service: any) => 
          service.id === appointmentFormData.serviceId && 
          service.staffs?.some((staffMember: any) => staffMember.id === staffId)
        ) ? appointmentFormData.serviceId : '') : appointmentFormData.serviceId
    });
  };

  const handleServiceChange = (serviceId: string) => {
    setAppointmentFormData({ 
      ...appointmentFormData, 
      serviceId,
      // Clear staff if they can't provide the selected service
      staffId: serviceId && appointmentFormData.staffId ? 
        (staff.some((staffMember: any) => 
          staffMember.id === appointmentFormData.staffId && 
          staffMember.services?.some((service: any) => service.id === serviceId)
        ) ? appointmentFormData.staffId : '') : appointmentFormData.staffId
    });
  };

  const handleClearStaff = () => {
    setAppointmentFormData({ ...appointmentFormData, staffId: '' });
  };

  const handleClearService = () => {
    setAppointmentFormData({ ...appointmentFormData, serviceId: '' });
  };

  // Form validation function
  const validateAppointmentForm = () => {
    const errors = [];
    
    if (!appointmentFormData.clientName.trim()) {
      errors.push('Client name is required');
    }
    if (!appointmentFormData.clientPhone.trim()) {
      errors.push('Client phone is required');
    }
    if (!appointmentFormData.clientEmail.trim()) {
      errors.push('Client email is required');
    }
    if (!appointmentFormData.scheduledDate) {
      errors.push('Schedule date is required');
    }
    if (!appointmentFormData.selectedTime) {
      errors.push('Schedule time is required');
    }
    if (!appointmentFormData.staffId && filteredStaff.length === 0) {
      errors.push('At least one staff member must be available');
    }
    if (!appointmentFormData.staffId && filteredStaff.length > 0) {
      errors.push('Please select a staff member');
    }
    if (!appointmentFormData.serviceId && filteredServices.length === 0) {
      errors.push('At least one service must be available');
    }
    if (!appointmentFormData.serviceId && filteredServices.length > 0) {
      errors.push('Please select a service');
    }
    
    return errors;
  };
  
  const timezone = getStoreTimezone();
  
  const todayAppointments = appointments.filter((apt: any) => {
    try {
      // Skip appointments with invalid or missing dates
      if (!apt.scheduledDate || typeof apt.scheduledDate !== 'string') {
        return false;
      }
      const appointmentDate = parseDate(apt.scheduledDate);
      const today = new Date();
      return isSameDay(appointmentDate, today, timezone);
    } catch (error) {
      // Skip appointments with invalid dates
      console.warn('Skipping appointment with invalid date:', apt.scheduledDate);
      return false;
    }
  });

  const averageRating = ratings.length > 0
    ? ratings.reduce((sum: number, rating: any) => sum + (Number(rating.rating) || 0), 0) / ratings.length
    : 0;

  // Use categorized appointment groups like in Appointments page
  const categorizedAppointments = categorizeAppointmentGroups(appointments);
  const { upcoming } = categorizedAppointments;

  // Filter upcoming appointment groups by staff and limit to 5
  const upcomingAppointments = upcoming
    .filter((group: AppointmentGroup) => selectedStaffId === 'all' || group.staffId === selectedStaffId)
    .slice(0, 5);

  const handleStaffFilterChange = (event: SelectChangeEvent<string>) => {
    setSelectedStaffId(event.target.value);
  };

  const statsCards = [
    {
      title: 'Today\'s Appointments',
      value: todayAppointments.length,
      icon: <CalendarMonth />,
      color: theme.palette.primary.main,
    },
    {
      title: 'Active Staff',
      value: staff.length,
      icon: <People />,
      color: theme.palette.secondary.main,
    },
    {
      title: 'Services Offered',
      value: services.length,
      icon: <RoomService />,
      color: theme.palette.success.main,
    },
    {
      title: 'Average Rating',
      value: averageRating.toFixed(1),
      icon: <Star />,
      color: theme.palette.warning.main,
    },
  ];

  return (
    <Box sx={{ 
      p: { xs: 2, md: 3 }, 
      maxWidth: '1200px', 
      mx: 'auto',
      minHeight: '100vh',
      bgcolor: 'background.default'
    }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: { xs: 'flex-start', md: 'center' },
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 2, md: 0 },
          mb: 3,
          pb: 2,
          borderBottom: 1,
          borderColor: 'divider'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="h4" component="h1" fontWeight={700} sx={{ mr: 2 }}>
              Dashboard
            </Typography>
            {store?.avatar && (
              <StoreAvatar
                storeAvatar={store.avatar}
                storeName={store.name}
                sx={{ 
                  width: 48, 
                  height: 48, 
                  mr: 2,
                  bgcolor: theme.palette.primary.main,
                  boxShadow: theme.shadows[3]
                }}
              />
            )}
          </Box>
          <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
            Welcome back! Here's what's happening{store?.name ? ` at ${store.name}` : ' at your barbershop'} today.
          </Typography>
        </Box>
      </motion.div>

      <Grid container spacing={3}>
        {/* Stats Cards Section */}
        <Grid item xs={12}>
          <Paper sx={{ 
            p: { xs: 2, md: 3 },
            borderRadius: 2,
            boxShadow: theme.shadows[1],
            bgcolor: 'background.paper'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight={600}>
                Performance Overview
              </Typography>
            </Box>
            <Grid container spacing={2}>
              {statsCards.map((card, index) => (
                <Grid item xs={12} sm={6} md={3} key={card.title}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <Box 
                      sx={{ 
                        p: 2,
                        borderRadius: 2,
                        border: 1,
                        borderColor: 'divider',
                        bgcolor: 'background.default',
                        height: '100%',
                        transition: 'all 0.3s ease-in-out',
                        '&:hover': {
                          borderColor: card.color,
                          transform: 'translateY(-1px)',
                          boxShadow: theme.shadows[2]
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                        <Box
                          sx={{
                            p: 0.75,
                            borderRadius: 1.5,
                            backgroundColor: card.color,
                            color: 'white',
                            mr: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          {card.icon}
                        </Box>
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography variant="h5" component="div" fontWeight={700} sx={{ mb: 0.25, lineHeight: 1.2 }}>
                            {card.value}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', lineHeight: 1.2 }}>
                            {card.title}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>

        {/* Upcoming Appointments */}
        <Grid item xs={12} md={8}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <Paper sx={{
              p: { xs: 2, md: 3 },
              borderRadius: 2,
              boxShadow: theme.shadows[1],
              bgcolor: 'background.paper',
              height: { xs: 'auto', md: 500 },
              minHeight: { xs: 300, md: 500 },
              display: 'flex',
              flexDirection: 'column'
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h6" fontWeight={600}>
                  Upcoming Appointments
                </Typography>
                <FormControl 
                  size="small" 
                  sx={{ 
                    minWidth: 140,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1.5
                    }
                  }}
                >
                  <InputLabel>Staff</InputLabel>
                  <Select
                    value={selectedStaffId}
                    label="Staff"
                    onChange={handleStaffFilterChange}
                  >
                    <MenuItem value="all">All Staff</MenuItem>
                    {staff.map((staffMember: any) => (
                      <MenuItem key={staffMember.id} value={staffMember.id}>
                        {staffMember.fullName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ 
                flex: 1, 
                overflow: 'auto',
                p: 1,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1.5,
                bgcolor: 'background.default'
              }}>
                <List sx={{ py: 0 }}>
                {upcomingAppointments.map((group: AppointmentGroup, index: number) => {
                  const formatTimeRangeForGroup = () => {
                    if (group.hasMultipleAppointments) {
                      return formatTimeRange(group.earliestTime, group.latestTime, timezone);
                    }
                    return formatTime(group.earliestTime, timezone);
                  };

                  return (
                    <ListItem 
                      key={group.id} 
                      sx={{ 
                        px: 2,
                        py: 1.5,
                        borderBottom: index < upcomingAppointments.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover,
                          borderRadius: 1.5,
                          mx: 0.5,
                          px: 1.5
                        },
                        alignItems: 'flex-start'
                      }}
                    >
                      <ListItemIcon sx={{ mr: 1.5, minWidth: 'auto', mt: 0.25 }}>
                        <Box
                          sx={{
                            p: 0.75,
                            borderRadius: 1.5,
                            backgroundColor: theme.palette.primary.main,
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 32,
                            height: 32
                          }}
                        >
                          <Schedule fontSize="small" />
                        </Box>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: { xs: 1, sm: 2 }, flexDirection: { xs: 'column', sm: 'row' } }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1, minWidth: 0 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography variant="body1" fontWeight={700} component="span" sx={{ color: 'text.primary' }}>
                                  {group.clientName || 'Unknown Client'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" component="span" sx={{ fontWeight: 500, display: { xs: 'none', sm: 'inline' } }}>
                                  •
                                </Typography>
                                <Typography variant="body2" color="text.secondary" component="span" sx={{ fontWeight: 500 }}>
                                  {group.hasMultipleAppointments
                                    ? `${group.appointments.length} services`
                                    : group.appointments[0]?.service?.name || 'No Service'
                                  }
                                </Typography>
                              </Box>
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                                with {group.staffName || 'Any Staff'}
                              </Typography>
                              {group.appointments[0]?.clientComment && (
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', fontStyle: 'italic', mt: 0.25 }}>
                                  "{group.appointments[0].clientComment}"
                                </Typography>
                              )}
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: { xs: 'row', sm: 'column' }, alignItems: { xs: 'center', sm: 'flex-end' }, gap: 0.5, minWidth: 'fit-content' }}>
                              <Typography variant="body2" color="text.primary" sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
                                {group.date}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>•</Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                                {formatTimeRangeForGroup()}
                              </Typography>
                            </Box>
                          </Box>
                        }
                        primaryTypographyProps={{ component: 'div' }}
                      />
                    </ListItem>
                  );
                })}
                {upcomingAppointments.length === 0 && (
                  <ListItem sx={{ py: 4, textAlign: 'center' }}>
                    <ListItemText
                      primary={
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                          No upcoming appointments
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary">
                          Your schedule is clear for now ✨
                        </Typography>
                      }
                    />
                  </ListItem>
                )}
              </List>
              </Box>
            </Paper>
          </motion.div>
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12} md={4}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <Paper sx={{
              p: { xs: 2, md: 3 },
              borderRadius: 2,
              boxShadow: theme.shadows[1],
              bgcolor: 'background.paper',
              height: { xs: 'auto', md: 500 },
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              zIndex: 1
            }}>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 4, position: 'relative', zIndex: 2 }}>
                Quick Actions
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: 2, 
                flex: 1,
                overflowY: 'auto',
                position: 'relative',
                zIndex: 1,
                pt: 1
              }}>
                <Box 
                  sx={{ 
                    p: 2, 
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1.5,
                    bgcolor: 'background.default',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': { 
                      borderColor: theme.palette.primary.main,
                      boxShadow: theme.shadows[2],
                      zIndex: 10
                    },
                    textDecoration: 'none',
                    color: 'inherit',
                    '&:visited': {
                      color: 'inherit'
                    },
                    position: 'relative'
                  }}
                  component={Link}
                  to={`/store/${storeId}/details`}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: 1.5,
                        backgroundColor: theme.palette.primary.main,
                        color: 'white',
                        mr: 2
                      }}
                    >
                      <Store fontSize="small" />
                    </Box>
                    <Box>
                      <Typography variant="body1" fontWeight={600} component="div" sx={{ mb: 0.25, color: 'inherit' }}>
                        Store Details
                      </Typography>
                      <Typography variant="body2" color="text.secondary" component="div" sx={{ fontSize: '0.875rem' }}>
                        View store information
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                
                <Box 
                  sx={{ 
                    p: 2, 
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1.5,
                    bgcolor: 'background.default',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': { 
                      borderColor: theme.palette.secondary.main,
                      boxShadow: theme.shadows[2],
                      zIndex: 10
                    },
                    position: 'relative'
                  }}
                  onClick={handleOpenAppointmentDialog}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: 1.5,
                        backgroundColor: theme.palette.secondary.main,
                        color: 'white',
                        mr: 2
                      }}
                    >
                      <CalendarMonth fontSize="small" />
                    </Box>
                    <Box>
                      <Typography variant="body1" fontWeight={600} component="div" sx={{ mb: 0.25 }}>
                        New Appointment
                      </Typography>
                      <Typography variant="body2" color="text.secondary" component="div" sx={{ fontSize: '0.875rem' }}>
                        Schedule a new booking
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                
                <Box 
                  sx={{ 
                    p: 2, 
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1.5,
                    bgcolor: 'background.default',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': { 
                      borderColor: theme.palette.success.main,
                      boxShadow: theme.shadows[2],
                      zIndex: 10
                    },
                    position: 'relative'
                  }}
                  onClick={handleOpenStaffDialog}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: 1.5,
                        backgroundColor: theme.palette.success.main,
                        color: 'white',
                        mr: 2
                      }}
                    >
                      <PersonAdd fontSize="small" />
                    </Box>
                    <Box>
                      <Typography variant="body1" fontWeight={600} component="div" sx={{ mb: 0.25 }}>
                        Add Staff
                      </Typography>
                      <Typography variant="body2" color="text.secondary" component="div" sx={{ fontSize: '0.875rem' }}>
                        Invite new team member
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                
                <Box 
                  sx={{ 
                    p: 2, 
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1.5,
                    bgcolor: 'background.default',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': { 
                      borderColor: theme.palette.warning.main,
                      boxShadow: theme.shadows[2],
                      zIndex: 10
                    },
                    position: 'relative'
                  }}
                  onClick={handleOpenServiceDialog}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: 1.5,
                        backgroundColor: theme.palette.warning.main,
                        color: 'white',
                        mr: 2
                      }}
                    >
                      <RoomService fontSize="small" />
                    </Box>
                    <Box>
                      <Typography variant="body1" fontWeight={600} component="div" sx={{ mb: 0.25 }}>
                        Add Service
                      </Typography>
                      <Typography variant="body2" color="text.secondary" component="div" sx={{ fontSize: '0.875rem' }}>
                        Create new service
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Paper>
          </motion.div>
        </Grid>

        {/* Overview Statistics */}
        <Grid item xs={12}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            <Paper sx={{ 
              p: { xs: 2, md: 3 },
              borderRadius: 2,
              boxShadow: theme.shadows[1],
              bgcolor: 'background.paper'
            }}>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
                Overview Statistics
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Box sx={{ 
                    p: 2.5,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1.5,
                    bgcolor: 'background.default',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ 
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      mb: 1
                    }}>
                      Total Appointments
                    </Typography>
                    <Typography variant="h4" fontWeight={700} color="primary" sx={{ mb: 2 }}>
                      {appointments.length}
                    </Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={Math.min((appointments.length / 50) * 100, 100)} 
                      sx={{ 
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: theme.palette.primary.light + '30',
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 3
                        }
                      }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ 
                    p: 2.5,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1.5,
                    bgcolor: 'background.default',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ 
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      mb: 1
                    }}>
                      Today's Bookings
                    </Typography>
                    <Typography variant="h4" fontWeight={700} color="secondary" sx={{ mb: 2 }}>
                      {todayAppointments.length}
                    </Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={Math.min((todayAppointments.length / 20) * 100, 100)} 
                      color="secondary"
                      sx={{ 
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: theme.palette.secondary.light + '30',
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 3
                        }
                      }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ 
                    p: 2.5,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1.5,
                    bgcolor: 'background.default',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ 
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      mb: 1
                    }}>
                      Customer Satisfaction
                    </Typography>
                    <Typography variant="h4" fontWeight={700} color="success" sx={{ mb: 2 }}>
                      {(averageRating * 20).toFixed(0)}%
                    </Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={averageRating * 20} 
                      color="success"
                      sx={{ 
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: theme.palette.success.light + '30',
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 3
                        }
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </motion.div>
        </Grid>
      </Grid>

      {/* Appointment Dialog */}
      <Dialog
        open={openAppointmentDialog}
        onClose={handleCloseAppointmentDialog}
        maxWidth="lg"
        fullWidth
        fullScreen={window.innerWidth < theme.breakpoints.values.sm}
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 3 },
            boxShadow: theme.shadows[8],
            maxHeight: { xs: '100vh', sm: '90vh' }
          }
        }}
      >
        <DialogTitle sx={{
          pb: 0,
          pt: { xs: 2, sm: 3 },
          px: { xs: 2, sm: 4 }
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', mb: 1 }}>
                New Appointment
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Fill in the details for your new appointment
              </Typography>
            </Box>
            <IconButton 
              onClick={handleCloseAppointmentDialog}
              sx={{ 
                borderRadius: 2,
                p: 1,
                '&:hover': { bgcolor: 'action.hover' }
              }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ px: { xs: 2, sm: 4 }, py: { xs: 2, sm: 3 } }}>
          <Grid container spacing={4}>
            {/* Left Column - Main Details */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, borderRadius: 2, bgcolor: 'background.default' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: 'text.primary' }}>
                  Appointment Details
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <TextField
                    fullWidth
                    label="Staff Member"
                    select
                    value={appointmentFormData.staffId}
                    onChange={(e) => handleStaffChange(e.target.value)}
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2
                      }
                    }}
                    InputProps={{
                      endAdornment: appointmentFormData.staffId ? (
                        <IconButton
                          size="small"
                          onClick={handleClearStaff}
                          sx={{ mr: 1 }}
                        >
                          <Close />
                        </IconButton>
                      ) : null,
                    }}
                  >
                    {filteredStaff.length === 0 ? (
                      <MenuItem value="" disabled>
                        {appointmentFormData.serviceId ? 'No staff available for this service' : 'No staff available'}
                      </MenuItem>
                    ) : (
                      filteredStaff.map((staffMember: any) => (
                        <MenuItem key={staffMember.id} value={staffMember.id}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Person sx={{ fontSize: 20, color: 'text.secondary' }} />
                            <Typography>{staffMember.fullName}</Typography>
                          </Box>
                        </MenuItem>
                      ))
                    )}
                  </TextField>

                  <TextField
                    fullWidth
                    label="Service"
                    select
                    value={appointmentFormData.serviceId}
                    onChange={(e) => handleServiceChange(e.target.value)}
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2
                      }
                    }}
                    InputProps={{
                      endAdornment: appointmentFormData.serviceId ? (
                        <IconButton
                          size="small"
                          onClick={handleClearService}
                          sx={{ mr: 1 }}
                        >
                          <Close />
                        </IconButton>
                      ) : null,
                    }}
                  >
                    {filteredServices.length === 0 ? (
                      <MenuItem value="" disabled>
                        {appointmentFormData.staffId ? 'No services available for this staff member' : 'No services available'}
                      </MenuItem>
                    ) : (
                      filteredServices.map((service: any) => (
                        <MenuItem key={service.id} value={service.id}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <RoomService sx={{ fontSize: 20, color: 'text.secondary' }} />
                            <Box>
                              <Typography>{service.name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formatGBP(service.price)} • {service.duration} min
                              </Typography>
                            </Box>
                          </Box>
                        </MenuItem>
                      ))
                    )}
                  </TextField>

                  {/* Enhanced Date Picker with Availability */}
                  <AvailabilityDatePicker
                    storeId={storeId || ''}
                    selectedDate={appointmentFormData.scheduledDate}
                    selectedStaffId={appointmentFormData.staffId}
                    onDateSelect={(date: string) => setAppointmentFormData({ ...appointmentFormData, scheduledDate: date, selectedTime: '' })}
                  />

                  {/* Time Slot Picker */}
                  <TimeSlotPicker
                    storeId={storeId || ''}
                    selectedStaffId={appointmentFormData.staffId}
                    selectedService={services.find((s: any) => s.id === appointmentFormData.serviceId) || null}
                    selectedDate={appointmentFormData.scheduledDate}
                    selectedTime={appointmentFormData.selectedTime}
                    onTimeSelect={(time) => setAppointmentFormData({ ...appointmentFormData, selectedTime: time })}
                    disabled={!appointmentFormData.staffId || !appointmentFormData.serviceId || !appointmentFormData.scheduledDate}
                  />
                </Box>
              </Paper>
            </Grid>

            {/* Right Column - Client Details */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, borderRadius: 2, bgcolor: 'background.default' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: 'text.primary' }}>
                  Client Information
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <TextField
                    fullWidth
                    label="Client Name"
                    value={appointmentFormData.clientName}
                    onChange={(e) => setAppointmentFormData({ ...appointmentFormData, clientName: e.target.value })}
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2
                      }
                    }}
                  />

                  <TextField
                    fullWidth
                    label="Client Phone"
                    value={appointmentFormData.clientPhone}
                    onChange={(e) => setAppointmentFormData({ ...appointmentFormData, clientPhone: e.target.value })}
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2
                      }
                    }}
                  />

                  <TextField
                    fullWidth
                    label="Client Email"
                    type="email"
                    value={appointmentFormData.clientEmail}
                    onChange={(e) => setAppointmentFormData({ ...appointmentFormData, clientEmail: e.target.value })}
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2
                      }
                    }}
                  />

                  <TextField
                    fullWidth
                    label="Comments"
                    multiline
                    rows={4}
                    value={appointmentFormData.clientComment}
                    onChange={(e) => setAppointmentFormData({ ...appointmentFormData, clientComment: e.target.value })}
                    placeholder="Any special instructions or notes..."
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2
                      }
                    }}
                  />
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions sx={{
          px: { xs: 2, sm: 4 },
          py: { xs: 2, sm: 3 },
          gap: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
          bgcolor: 'background.default',
          flexDirection: { xs: 'column', sm: 'row' },
          '& > button': { width: { xs: '100%', sm: 'auto' } }
        }}>
          <Button 
            onClick={handleCloseAppointmentDialog}
            variant="outlined"
            size="large"
            sx={{ 
              borderRadius: 2,
              px: 4,
              py: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              minWidth: 120
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleAppointmentSubmit}
            disabled={
              !appointmentFormData.clientName.trim() ||
              !appointmentFormData.clientPhone.trim() ||
              !appointmentFormData.clientEmail.trim() ||
              !appointmentFormData.scheduledDate ||
              !appointmentFormData.selectedTime ||
              !appointmentFormData.staffId ||
              !appointmentFormData.serviceId
            }
            size="large"
            sx={{ 
              borderRadius: 2,
              px: 4,
              py: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              minWidth: 120,
              boxShadow: theme.shadows[3],
              '&:hover': {
                boxShadow: theme.shadows[6]
              }
            }}
          >
            Create Appointment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Staff Dialog */}
      <Dialog
        open={openStaffDialog}
        onClose={handleCloseStaffDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: theme.shadows[8],
          }
        }}
      >
        <DialogTitle sx={{ 
          p: 3, 
          pb: 1,
          borderBottom: 1,
          borderColor: 'divider'
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight={600}>
              Add New Staff Member
            </Typography>
            <IconButton 
              onClick={handleCloseStaffDialog}
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  color: theme.palette.error.main,
                  bgcolor: theme.palette.error.main + '10',
                }
              }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={3} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="First Name"
                value={staffFormData.firstName}
                onChange={(e) => setStaffFormData({ ...staffFormData, firstName: e.target.value })}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={staffFormData.lastName}
                onChange={(e) => setStaffFormData({ ...staffFormData, lastName: e.target.value })}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Username"
                value={staffFormData.username}
                onChange={(e) => setStaffFormData({ ...staffFormData, username: e.target.value })}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={staffFormData.email}
                onChange={(e) => setStaffFormData({ ...staffFormData, email: e.target.value })}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Role</InputLabel>
                <Select
                  value={staffFormData.role}
                  label="Role"
                  onChange={(e) => setStaffFormData({ ...staffFormData, role: e.target.value as StaffRole })}
                  sx={{
                    borderRadius: 1.5,
                  }}
                >
                  <MenuItem value={STAFF_ROLES.STAFF}>Staff</MenuItem>
                  <MenuItem value={STAFF_ROLES.ADMIN}>Admin</MenuItem>
                  <MenuItem value={STAFF_ROLES.OWNER}>Owner</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={staffFormData.password}
                onChange={(e) => setStaffFormData({ ...staffFormData, password: e.target.value })}
                helperText="Leave empty to auto-generate a password"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Services</InputLabel>
                <Select
                  multiple
                  value={staffFormData.services}
                  onChange={(e) => setStaffFormData({ ...staffFormData, services: e.target.value as string[] })}
                  input={<OutlinedInput label="Services" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const service = services.find((s: any) => s.id === value);
                        return (
                          <Chip
                            key={value}
                            label={service?.name || value}
                            size="small"
                            sx={{
                              bgcolor: 'background.default',
                              border: 1,
                              borderColor: 'divider'
                            }}
                          />
                        );
                      })}
                    </Box>
                  )}
                  sx={{
                    borderRadius: 1.5,
                  }}
                >
                  {services.map((service: any) => (
                    <MenuItem key={service.id} value={service.id}>
                      <Checkbox checked={staffFormData.services.includes(service.id)} />
                      <MuiListItemText 
                        primary={service.name} 
                        secondary={`${service.duration}min - $${service.price}`}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            {/* Staff Active Toggle */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={staffFormData.isActive}
                    onChange={(e) => setStaffFormData({ ...staffFormData, isActive: e.target.checked })}
                    color="primary"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="body1" fontWeight={600}>
                      Staff Active
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {staffFormData.isActive 
                        ? 'Staff assignment is active and available for appointments' 
                        : 'Staff assignment is inactive and excluded from appointments'
                      }
                    </Typography>
                  </Box>
                }
                sx={{ mt: 2, mb: 1 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ 
          p: 3, 
          pt: 2,
          borderTop: 1,
          borderColor: 'divider',
          gap: 2
        }}>
          <Button 
            onClick={handleCloseStaffDialog}
            sx={{
              borderRadius: 1.5,
              px: 3,
              py: 1,
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleStaffSubmit}
            sx={{
              borderRadius: 1.5,
              px: 3,
              py: 1,
              fontWeight: 600,
              boxShadow: theme.shadows[2],
              '&:hover': {
                boxShadow: theme.shadows[4],
              }
            }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Service Dialog */}
      <Dialog
        open={openServiceDialog}
        onClose={handleCloseServiceDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: theme.shadows[3]
          }
        }}
      >
        <DialogTitle sx={{ p: 3, pb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ 
                p: 1,
                borderRadius: 1.5,
                bgcolor: theme.palette.primary.main,
                color: 'white',
                mr: 2
              }}>
                <RoomService fontSize="small" />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography variant="h5" fontWeight={600}>
                  Add New Service
                </Typography>
                <Chip
                  label={serviceFormData.isActive ? "Active" : "Inactive"}
                  color={serviceFormData.isActive ? "success" : "default"}
                  size="small"
                  sx={{ fontSize: '0.75rem' }}
                />
              </Box>
            </Box>
            <IconButton 
              onClick={handleCloseServiceDialog}
              sx={{ 
                bgcolor: 'background.default',
                '&:hover': { bgcolor: theme.palette.error.main, color: 'white' }
              }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={3} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Service Name"
                value={serviceFormData.name}
                onChange={(e) => setServiceFormData({ ...serviceFormData, name: e.target.value })}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <RoomService color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Duration (minutes)"
                type="number"
                value={serviceFormData.duration}
                onChange={(e) => setServiceFormData({ ...serviceFormData, duration: Number(e.target.value) })}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AccessTime color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Price (£)"
                type="number"
                value={serviceFormData.price}
                onChange={(e) => setServiceFormData({ ...serviceFormData, price: Number(e.target.value) })}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CurrencyPound color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={serviceFormData.isActive}
                    onChange={(e) => setServiceFormData({ ...serviceFormData, isActive: e.target.checked })}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body1">
                    Service Active
                  </Typography>
                }
                sx={{ mt: 1 }}
              />
            </Grid>
            
            {serviceFormData.duration > 0 && serviceFormData.price > 0 && (
              <Grid item xs={12}>
                <Paper sx={{ 
                  p: 2,
                  borderRadius: 1.5,
                  bgcolor: alpha(theme.palette.info.main, 0.05),
                  border: 1,
                  borderColor: alpha(theme.palette.info.main, 0.2)
                }}>
                  <Typography variant="body2" color="info.main" fontWeight={600}>
                    💡 Rate: {formatGBP(serviceFormData.price / serviceFormData.duration)} per minute
                  </Typography>
                </Paper>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button 
            onClick={handleCloseServiceDialog}
            variant="outlined"
            sx={{
              borderRadius: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              px: 3
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleServiceSubmit}
            disabled={!serviceFormData.name || !serviceFormData.duration || serviceFormData.price < 0}
            sx={{
              borderRadius: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              boxShadow: theme.shadows[2]
            }}
          >
            Create Service
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Dashboard;
